import { Amount, CurrencyType } from '@enter/core';
import React from 'react';
import * as S from './patient-collections.styles';

/**
 * Format money for this screen. Do not use renderAmount/numeral — `$0,0.00` is easy to
 * mis-parse, and an Amount-shaped object with a missing inner `amount` renders as '-'.
 */
export function asCollectionAmount(value: unknown): Amount | undefined {
    if (value == null || value === '') {
        return undefined;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value)
            ? { amount: value.toFixed(2), currency: CurrencyType.USD }
            : undefined;
    }
    if (typeof value === 'string') {
        const t = value.trim();
        return t ? { amount: t, currency: CurrencyType.USD } : undefined;
    }
    if (typeof value !== 'object') {
        return undefined;
    }
    const o = value as Record<string, unknown>;
    const raw = o.amount ?? o.Amount ?? o.value ?? o.total ?? o.totalAmount;
    if (raw && typeof raw === 'object') {
        return asCollectionAmount(raw);
    }
    if (raw == null || raw === '') {
        return undefined;
    }
    const currency = (o.currency ?? o.Currency) as CurrencyType | undefined;
    if (typeof raw === 'number') {
        return Number.isFinite(raw)
            ? { amount: raw.toFixed(2), currency: currency ?? CurrencyType.USD }
            : undefined;
    }
    const t = String(raw).trim();
    return t ? { amount: t, currency: currency ?? CurrencyType.USD } : undefined;
}

export function renderCollectionAmount(value: unknown): string {
    const a = asCollectionAmount(value);
    if (!a) {
        return '—';
    }
    const n = Number(String(a.amount).replace(/[$,]/g, ''));
    if (!Number.isFinite(n)) {
        return '—';
    }
    const code = !a.currency || a.currency === CurrencyType.USD ? 'USD' : String(a.currency);
    try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(n);
    } catch {
        return `$${n.toFixed(2)}`;
    }
}

export const amountNumber = (a?: Amount | unknown): number => {
    const n = Number(asCollectionAmount(a)?.amount ?? 0);
    return Number.isFinite(n) ? n : 0;
};

/** DataTable flex default maxWidth is 400; money cols need room for $1,234.56. */
export const amountColumnSize = {
    width: 112,
    minWidth: 96,
    maxWidth: 140,
} as const;

/** Fits Set Balance / Apply Payments + Confirm Balance without shrinking labels. */
export const actionColumnSize = {
    width: 340,
    minWidth: 328,
    maxWidth: 360,
} as const;

export const PatientNameCell: React.FC<{
    name: string;
    sub: string;
    onOpen: () => void;
}> = ({ name, sub, onOpen }) => (
    <S.Cell>
        <S.PatientNameLink
            variant="unstyled"
            onClick={e => {
                e.stopPropagation();
                onOpen();
            }}>
            {name}
        </S.PatientNameLink>
        <S.CellSecondary>{sub || '—'}</S.CellSecondary>
    </S.Cell>
);

export const BalanceDueCell: React.FC<{ amount?: Amount }> = ({ amount }) => {
    const n = amountNumber(amount);
    const credit = n < -0.004;
    const zero = Math.abs(n) <= 0.004;
    return (
        <S.Cell>
            <S.BalanceDueValue $muted={zero} $credit={credit}>
                {renderCollectionAmount(amount)}
            </S.BalanceDueValue>
            {credit ? <S.BalanceTag>Credit · owed to patient</S.BalanceTag> : null}
        </S.Cell>
    );
};

export const PaidAmountCell: React.FC<{ amount?: Amount }> = ({ amount }) => {
    const n = amountNumber(amount);
    return (
        <S.Cell>
            <S.MutedAmount>{n > 0.004 ? `−${renderCollectionAmount(amount)}` : '—'}</S.MutedAmount>
        </S.Cell>
    );
};

export const TableEmpty: React.FC<{ message: string }> = ({ message }) => (
    <S.TableEmpty>
        <S.TableEmptyMessage>{message}</S.TableEmptyMessage>
    </S.TableEmpty>
);
